/**
 * UnitPickRow — one piece of the home's equipment as a thing to tick (PROPOSAL-EQUIPMENT-02 r2,
 * Seth 2026-10-29: "I hate the pills. The model and serial number look ugly. Refresh and better
 * design"). A row, not a pill: the kind and the room lead, the make and model read as one quiet
 * line, the serial sits beside it as data in a mono face, and the tick is at the end where a
 * thumb lands. Staff-facing — the office builder (Working On, Covers) and the tech's call path
 * (Equipment step, the plan's Covers, the invoice plan sheet); homeowners get the tiles.
 *
 * ServiceTitan and FieldEdge pick job equipment from exactly this kind of list — type · make ·
 * model · serial per row with a checkbox — and nobody puts a serial in a pill.
 *
 * Tones: "plan" (emerald — on the maintenance plan) · "work" (amber — this visit is about it).
 */
import { Check } from "lucide-react";
import { cn } from "@/lib/cn";
import { equipStyle } from "@/lib/equipmentIcons";

export interface UnitPickRowProps {
  /** The classifier's category key ("furnace", "rooftop" …) — picks the icon and its tint. */
  kind: string | null | undefined;
  /** "Rooftop Unit", "Furnace" … */
  label: string;
  /** The room / area it serves, already read like a person says it ("Main house"). */
  loc?: string | null;
  brand?: string | null;
  model?: string | null;
  serial?: string | null;
  /** The install year when the record has a real one (DATE-TRUTH). */
  year?: number | null;
  /** A short trailing note ("field-added", "pre-marked"). */
  note?: string | null;
  on: boolean;
  tone: "plan" | "work";
  disabled?: boolean;
  onToggle: () => void;
  /** Phone rows breathe a little more. */
  size?: "sm" | "md";
}

export function UnitPickRow({ kind, label, loc, brand, model, serial, year, note, on, tone, disabled, onToggle, size = "sm" }: UnitPickRowProps) {
  const st = equipStyle(kind);
  const Icon = st.Icon;
  const emerald = tone === "plan";
  const hasLine2 = !!(brand || model || serial || year || note);
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={on}
      disabled={disabled}
      onClick={onToggle}
      className={cn(
        "flex w-full items-center gap-3 rounded-xl border text-left transition-colors disabled:cursor-default",
        size === "md" ? "px-3.5 py-3" : "px-3 py-2",
        on
          ? emerald ? "border-emerald-300 bg-emerald-50/50" : "border-amber-300 bg-amber-50/60"
          : "border-hairline bg-white hover:border-slate-300 active:bg-slate-50",
      )}
    >
      <span className={cn("grid shrink-0 place-items-center rounded-lg", size === "md" ? "h-10 w-10" : "h-9 w-9", st.tile, !on && "opacity-60")}>
        <Icon className={size === "md" ? "h-5 w-5" : "h-4 w-4"} strokeWidth={1.75} />
      </span>
      <span className="min-w-0 flex-1">
        <span className={cn("block truncate font-semibold", size === "md" ? "text-[14px]" : "text-[13px]", on ? "text-slate-900" : "text-slate-600")}>
          {label}{loc ? <span className="font-normal text-slate-500"> · {loc}</span> : null}
        </span>
        {hasLine2 && (
          <span className={cn("mt-0.5 flex min-w-0 flex-wrap items-baseline gap-x-2 text-slate-500", size === "md" ? "text-[12px]" : "text-[11.5px]")}>
            {(brand || model) && (
              <span className="truncate">
                {brand}{brand && model ? " " : ""}
                {model && <span className={cn("font-mono tracking-tight", size === "md" ? "text-[11.5px]" : "text-[11px]", on ? "text-slate-700" : "text-slate-500")}>{model}</span>}
              </span>
            )}
            {serial && <span className={cn("shrink-0 font-mono tracking-tight text-slate-400", size === "md" ? "text-[11px]" : "text-[10.5px]")}>S/N {serial}</span>}
            {year && <span className="shrink-0 text-slate-400">{year}</span>}
            {note && <span className="shrink-0 text-slate-400">{note}</span>}
          </span>
        )}
      </span>
      <span
        aria-hidden="true"
        className={cn(
          "grid shrink-0 place-items-center rounded-full border-2 transition-colors",
          size === "md" ? "h-6 w-6" : "h-5 w-5",
          on ? emerald ? "border-emerald-600 bg-emerald-600 text-white" : "border-amber-500 bg-amber-500 text-white" : "border-slate-300 bg-white",
        )}
      >
        {on && <Check className={size === "md" ? "h-3.5 w-3.5" : "h-3 w-3"} strokeWidth={3} />}
      </span>
    </button>
  );
}
